import { X, Play } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import type { WatchHistory } from '../../../shared/types/watch-history.types'
import { useIsTVDevice } from '../hooks/useTVNavigation'

interface SourceSelectionDialogProps {
  isOpen: boolean
  onClose: () => void
  movieTitle: string
  sources: WatchHistory[]
  onSourceSelect: (source: WatchHistory) => void
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

const formatTimestamp = (currentSeconds: number, totalSeconds: number): string => {
  return `${formatDuration(currentSeconds)} / ${formatDuration(totalSeconds)}`
}

const getSourceBadgeColor = (sourceType: string): string => {
  switch (sourceType) {
    case 'vidfast':
      return 'bg-purple-600/90 text-white'
    case 'vidlink':
      return 'bg-pink-600/90 text-white'
    case 'gdrive':
      return 'bg-blue-600/90 text-white'
    case 'filestream':
      return 'bg-green-600/90 text-white'
    default:
      return 'bg-gray-600/90 text-white'
  }
}

const getSourceLabel = (sourceType: string): string => {
  switch (sourceType) {
    case 'vidfast':
      return 'VidFast'
    case 'vidlink':
      return 'VidLink'
    case 'gdrive':
      return 'Google Drive'
    case 'filestream':
      return 'FileStream'
    default:
      return sourceType
  }
}

const extractQualityFromFilename = (source: WatchHistory): string | null => {
  // Try to extract quality from quality field first
  if (source.quality) {
    return source.quality
  }
  
  // Try to extract from source_id or telegram_file_id
  const filename = source.source_id || source.telegram_file_id || ''
  const qualityMatch = filename.match(/(\d{3,4}p)/i)
  return qualityMatch ? qualityMatch[1] : null
}

const getFileName = (source: WatchHistory): string | null => {
  // Get the actual file name from source_id or telegram_file_id
  const filename = source.source_id || source.telegram_file_id || ''
  
  // If it's a long filename, truncate it smartly
  if (filename.length > 50) {
    return filename.substring(0, 47) + '...'
  }
  
  return filename || null
}

const shouldShowFileName = (sources: WatchHistory[]): boolean => {
  // Count sources by type, excluding streaming-only providers that aren't files
  // Backend merges VidFast and VidLink individually (keep most recent of each)
  const sourcesByType = sources.reduce((acc, source) => {
    if (source.source_type !== 'vidfast' && source.source_type !== 'vidlink') {
      acc[source.source_type] = (acc[source.source_type] || 0) + 1
    }
    return acc
  }, {} as Record<string, number>)
  
  // Show filename if any file-based source type has multiple entries
  return Object.values(sourcesByType).some(count => count > 1)
}

export default function SourceSelectionDialog({
  isOpen,
  onClose,
  movieTitle,
  sources,
  onSourceSelect
}: SourceSelectionDialogProps) {
  const isTV = useIsTVDevice()
  const [focusedSourceIndex, setFocusedSourceIndex] = useState(0)
  const sourceRefs = useRef<(HTMLButtonElement | null)[]>([])
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  // Check if we should show filenames (multiple files from same source type)
  const showFilenames = shouldShowFileName(sources)
  
  // Check if we have multiple qualities (for showing quality badges when NOT showing filenames)
  const hasMultipleQualities = !showFilenames && sources.length > 1 && sources.some(s => 
    extractQualityFromFilename(s) !== null
  )

  // Reset focused index when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFocusedSourceIndex(0)
    }
  }, [isOpen])

  // Auto-focus first source button when dialog opens on TV
  useEffect(() => {
    if (isOpen && isTV) {
      // Reset focus index
      setFocusedSourceIndex(0)
      // Retry mechanism to ensure button is available
      const tryFocus = (attempt = 0) => {
        const maxAttempts = 10
        const firstButton = sourceRefs.current[0]
        if (firstButton) {
          firstButton.focus()
          // Scroll into view to ensure button is visible
          firstButton.scrollIntoView({ block: 'nearest', behavior: 'auto' })
        } else if (attempt < maxAttempts) {
          setTimeout(() => tryFocus(attempt + 1), 50)
        }
      }
      setTimeout(() => tryFocus(), 100)
    }
  }, [isOpen, isTV])

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen || !isTV) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if dialog is open and focused
      if (!isOpen) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        const nextIndex = (focusedSourceIndex + 1) % sources.length
        setFocusedSourceIndex(nextIndex)
        setTimeout(() => {
          sourceRefs.current[nextIndex]?.focus()
        }, 0)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        const prevIndex = focusedSourceIndex === 0 ? sources.length - 1 : focusedSourceIndex - 1
        setFocusedSourceIndex(prevIndex)
        setTimeout(() => {
          sourceRefs.current[prevIndex]?.focus()
        }, 0)
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        if (focusedSourceIndex >= 0 && focusedSourceIndex < sources.length) {
          onSourceSelect(sources[focusedSourceIndex])
        }
      } else if (e.key === 'Escape' || e.key === 'Backspace') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    // Use capture phase to catch events before they bubble
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, isTV, focusedSourceIndex, sources, onSourceSelect, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/90 z-[99]"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={onClose}
            onKeyDown={(e) => {
              // Prevent event from bubbling to page handlers
              e.stopPropagation()
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Prevent event from bubbling to page handlers
                e.stopPropagation()
              }}
              className="bg-black/95 rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden tv-modal"
            >
              {/* Header */}
              <div className="px-8 py-6 border-b border-gray-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold text-white mb-2">
                      {movieTitle}
                    </h2>
                    <p className="text-gray-400 text-sm">
                      Choose a source to continue watching
                    </p>
                  </div>
                  <button
                    ref={closeButtonRef}
                    onClick={onClose}
                    onKeyDown={(e) => {
                      if (isTV && (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape' || e.key === 'Backspace')) {
                        e.preventDefault()
                        onClose()
                      }
                    }}
                    className={`text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full ml-4 ${isTV ? 'tv-focusable-item' : ''}`}
                    tabIndex={isTV ? 0 : undefined}
                    aria-label="Close"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Sources List */}
              <div 
                className="overflow-y-auto max-h-[calc(85vh-140px)] px-8 py-6"
                onKeyDown={(e) => {
                  // Prevent event from bubbling
                  e.stopPropagation()
                }}
              >
                <div className="space-y-4">
                  {sources.map((source, index) => {
                    const progressPercentage = source.total_duration_seconds > 0
                      ? Math.floor((source.playback_position_seconds / source.total_duration_seconds) * 100)
                      : 0
                    
                    const quality = extractQualityFromFilename(source)
                    const filename = getFileName(source)

                    return (
                      <motion.button
                        key={source.id}
                        ref={(el) => {
                          sourceRefs.current[index] = el
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => onSourceSelect(source)}
                        onFocus={() => {
                          setFocusedSourceIndex(index)
                        }}
                        className={`w-full bg-gray-900/50 hover:bg-gray-800/80 rounded-lg p-5 transition-all duration-200 group border border-gray-800 hover:border-gray-700 ${isTV ? 'tv-focusable-item' : ''}`}
                        tabIndex={isTV ? 0 : undefined}
                      >
                        <div className="flex items-center gap-4">
                          {/* Play Icon */}
                          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 group-hover:bg-red-600 flex items-center justify-center transition-all duration-200">
                            <Play className="w-5 h-5 text-white fill-white" />
                          </div>

                          {/* Content */}
                          <div className="flex-1 text-left">
                            {/* Source Badge and Filename/Quality */}
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`px-2.5 py-1 rounded text-xs font-semibold ${getSourceBadgeColor(source.source_type)}`}>
                                {getSourceLabel(source.source_type)}
                              </span>
                              
                              {/* Show filename if multiple files from same source, otherwise show quality */}
                              {showFilenames && filename ? (
                                <span className="px-2.5 py-1 rounded text-xs font-medium bg-gray-700 text-gray-300 max-w-md truncate">
                                  {filename}
                                </span>
                              ) : quality && hasMultipleQualities ? (
                                <span className="px-2.5 py-1 rounded text-xs font-semibold bg-gray-700 text-white">
                                  {quality}
                                </span>
                              ) : null}
                              
                              <span className="text-gray-500 text-xs">
                                • {formatTimestamp(source.playback_position_seconds, source.total_duration_seconds)}
                              </span>
                            </div>

                            {/* Progress Bar */}
                            <div className="relative h-1.5 bg-gray-800 rounded-full overflow-hidden mb-2">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercentage}%` }}
                                transition={{ delay: index * 0.05 + 0.1, duration: 0.4 }}
                                className="absolute inset-y-0 left-0 bg-red-600 rounded-full"
                              />
                            </div>

                            {/* Progress Text */}
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-400">
                                {progressPercentage}% complete
                              </span>
                              <span className="text-gray-600">
                                Last watched {new Date(source.last_watched_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.button>
                    )
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

